import { useEffect, useRef, useState } from "react";
import { api, API } from "@/App";
import MaskCanvas from "@/components/MaskCanvas";
import { LicenceBar } from "@/components/LicenceBar";
import { Upload, Loader2, Square, Brush, Eraser, Wand2, Download as DownloadIcon, RotateCcw } from "lucide-react";

const CHUNK = 4 * 1024 * 1024;
const TOOLS = [
  { id: "bbox", label: "Box", icon: Square },
  { id: "brush", label: "Brush", icon: Brush },
  { id: "eraser", label: "Exclude", icon: Eraser },
];

async function uploadVideo(file, onPct) {
  const { data } = await api.post("/matte/sessions", { fps: 25, source: "web" });
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
    await api.put(`/matte/sessions/${data.session_id}/video?index=${i}&total=${total}`, blob, {
      headers: { "Content-Type": "application/octet-stream" },
    });
    onPct(Math.round(((i + 1) / total) * 100));
  }
  return data.session_id;
}

function Dropzone({ onFile, busy, pct }) {
  return (
    <label
      className="flex flex-col items-center justify-center gap-3 border border-dashed border-[#2a2f3d] hover:border-[#e69d27] transition-colors bg-[#0e1013] py-16 cursor-pointer"
      data-testid="studio-dropzone"
    >
      {busy ? <Loader2 className="spin text-[#e69d27]" size={28} /> : <Upload className="text-[#e69d27]" size={28} />}
      <span className="font-display uppercase text-lg">{busy ? `Uploading ${pct}%` : "Drop a clip with a character"}</span>
      <span className="font-mono text-xs text-[#9ba3b4]">mp4 / mov / webm · up to 1 GB · any length</span>
      <input
        type="file"
        accept="video/*"
        className="hidden"
        disabled={busy}
        onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        data-testid="studio-file-input"
      />
    </label>
  );
}

function SelectionPanel({ session, onStart, starting }) {
  const [anchor, setAnchor] = useState(0);
  const [dims, setDims] = useState({ w: session.width || 16, h: session.height || 9 });
  const [tool, setTool] = useState("bbox");
  const [mask, setMask] = useState({ bbox: null, strokes: [] });
  const [brush, setBrush] = useState(0.04);
  const [quality, setQuality] = useState("fine");
  const [soft, setSoft] = useState(0.35);
  const [hint, setHint] = useState("");
  const src = `${API}/matte/sessions/${session.session_id}/frame/${anchor}.jpg`;
  const hasSel = (mask.bbox && mask.bbox.w > 0.01) || mask.strokes.length > 0 || hint.trim();

  useEffect(() => {
    const i = new Image();
    i.onload = () => setDims({ w: i.naturalWidth, h: i.naturalHeight });
    i.src = src;
  }, [src]);

  const start = () =>
    onStart({
      bbox: mask.bbox && mask.bbox.w > 0.01 ? mask.bbox : null,
      strokes: mask.strokes.map((s) => [s.x, s.y, s.erase ? -s.r : s.r]),
      text_prompt: hint,
      anchor_frame: anchor,
      quality,
      edge_softness: soft,
    });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5" data-testid="studio-selection">
      <div>
        <MaskCanvas imgSrc={src} imgDims={dims} tool={tool} mask={mask} onMaskChange={setMask} brushSize={brush} />
        <div className="flex items-center gap-3 mt-3 font-mono text-xs text-[#9ba3b4]">
          <span>frame {anchor}</span>
          <input
            type="range" min={0} max={Math.max(0, (session.frame_count || 1) - 1)} value={anchor}
            onChange={(e) => setAnchor(Number(e.target.value))}
            className="flex-1 accent-[#e69d27]" data-testid="studio-anchor-slider"
          />
          <span>{session.frame_count} frames</span>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <div className="chip mb-2">Select the character</div>
          <div className="grid grid-cols-3 gap-2">
            {TOOLS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTool(id)}
                className={`${tool === id ? "btn-amber" : "btn-ghost"} px-2 py-2 text-xs flex flex-col items-center gap-1`}
                data-testid={`studio-tool-${id}`}
              >
                <Icon size={14} />{label}
              </button>
            ))}
          </div>
          {tool !== "bbox" && (
            <input type="range" min={0.01} max={0.15} step={0.005} value={brush}
              onChange={(e) => setBrush(Number(e.target.value))}
              className="w-full mt-3 accent-[#00e5ff]" data-testid="studio-brush-size" />
          )}
          <button className="btn-ghost px-3 py-1 text-xs mt-2 flex items-center gap-1"
            onClick={() => setMask({ bbox: null, strokes: [] })} data-testid="studio-clear-selection">
            <RotateCcw size={12} /> clear selection
          </button>
        </div>
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#9ba3b4]">Character hint (optional)</span>
          <input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="e.g. woman in red"
            className="w-full mt-1 bg-[#0e1013] border border-[#2a2f3d] px-3 py-2 text-sm focus:border-[#e69d27] outline-none"
            data-testid="studio-hint-input" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[["fine", "Fine edges"], ["fast", "Fast"]].map(([id, label]) => (
            <button key={id} onClick={() => setQuality(id)}
              className={`${quality === id ? "btn-amber" : "btn-ghost"} px-2 py-2 text-xs`}
              data-testid={`studio-quality-${id}`}>{label}</button>
          ))}
        </div>
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#9ba3b4]">Edge softness {soft.toFixed(2)}</span>
          <input type="range" min={0} max={1} step={0.05} value={soft} onChange={(e) => setSoft(Number(e.target.value))}
            className="w-full accent-[#e69d27]" data-testid="studio-softness-slider" />
        </label>
        <button disabled={!hasSel || starting} onClick={start}
          className="btn-amber w-full px-4 py-3 flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="studio-pull-alpha-btn">
          {starting ? <Loader2 className="spin" size={16} /> : <Wand2 size={16} />} Pull alpha
        </button>
        {!hasSel && <p className="text-xs text-[#5c6479]">Draw a box or brush over the character first.</p>}
      </div>
    </div>
  );
}

function SyncedTrio({ base, labels = ["Original", "Alpha matte"], files = ["source.mp4", "matte.mp4"], testid }) {
  const refs = [useRef(null), useRef(null)];
  useEffect(() => {
    const lead = refs[0].current;
    if (!lead) return undefined;
    const sync = () => refs.slice(1).forEach((r) => {
      if (r.current && Math.abs(r.current.currentTime - lead.currentTime) > 0.08) r.current.currentTime = lead.currentTime;
    });
    lead.addEventListener("timeupdate", sync);
    return () => lead.removeEventListener("timeupdate", sync);
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={testid}>
      {files.map((f, i) => (
        <figure key={f} className="bg-black border border-[#2a2f3d]">
          <video ref={refs[i]} src={`${base}/${f}`} muted loop autoPlay playsInline className="w-full aspect-video object-contain"
            data-testid={`${testid}-video-${i}`} />
          <figcaption className="font-mono text-[11px] uppercase tracking-widest text-[#9ba3b4] px-3 py-2">{labels[i]}</figcaption>
        </figure>
      ))}
    </div>
  );
}

function Results({ session, onReset }) {
  const base = `${API}/matte/sessions/${session.session_id}/out`;
  return (
    <div className="space-y-4" data-testid="studio-results">
      <SyncedTrio base={base} testid="studio-trio" />
      <div className="flex flex-wrap gap-2">
        <a href={`${base}/character.mov`} className="btn-amber px-4 py-2 text-xs flex items-center gap-2" data-testid="studio-download-prores">
          <DownloadIcon size={14} /> Character + alpha (ProRes 4444)
        </a>
        <a href={`${base}/matte.mp4`} download className="btn-ghost px-4 py-2 text-xs flex items-center gap-2" data-testid="studio-download-matte">
          <DownloadIcon size={14} /> Matte (mp4)
        </a>
        <button onClick={onReset} className="btn-ghost px-4 py-2 text-xs ml-auto" data-testid="studio-new-clip-btn">New clip</button>
      </div>
    </div>
  );
}

export default function MatteStudio() {
  const [sid, setSid] = useState(null);
  const [session, setSession] = useState(null);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lic, setLic] = useState(null);
  const [licTick, setLicTick] = useState(0);

  useEffect(() => {
    if (!sid) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/matte/sessions/${sid}`);
        if (alive) setSession(data);
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { alive = false; clearInterval(t); };
  }, [sid]);

  const onFile = async (file) => {
    setErr(null); setBusy(true); setPct(0);
    try { setSid(await uploadVideo(file, setPct)); } catch (e) { setErr(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const onStart = async (body) => {
    setErr(null); setBusy(true);
    try { await api.post(`/matte/sessions/${sid}/start`, body); setSession((s) => ({ ...s, status: "PROCESSING" })); setLicTick((t) => t + 1); }
    catch (e) { setErr(e.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };
  const reset = () => { setSid(null); setSession(null); setErr(null); setLicTick((t) => t + 1); };
  const st = session?.status;

  return (
    <div data-testid="matte-studio">
      <LicenceBar onChange={setLic} refreshKey={licTick} />
      {err && <div className="mb-4 border border-[#ff453a] text-[#ff453a] px-3 py-2 text-sm" data-testid="studio-error">{String(err)}</div>}
      {!sid && lic && <Dropzone onFile={onFile} busy={busy} pct={pct} />}
      {sid && (!st || st === "UPLOADING" || st === "EXTRACTING") && (
        <div className="flex items-center gap-3 py-16 justify-center text-[#9ba3b4]" data-testid="studio-extracting">
          <Loader2 className="spin text-[#e69d27]" size={20} /> reading frames…
        </div>
      )}
      {st === "READY" && <SelectionPanel session={session} onStart={onStart} starting={busy} />}
      {st === "PROCESSING" && (
        <div className="py-12 space-y-3" data-testid="studio-processing">
          <div className="flex items-center gap-3"><Loader2 className="spin text-[#e69d27]" size={18} />
            <span className="font-display uppercase text-lg">Tracking character</span>
            <span className="font-mono text-xs text-[#9ba3b4] ml-auto" data-testid="studio-progress-stage">{session?.progress?.stage || "queued"}</span>
          </div>
          <div className="h-[3px] bg-[#2a2f3d]"><div className="h-full bg-[#e69d27] transition-[width] duration-500"
            style={{ width: `${Math.round(((session?.progress?.done || 0) / (session?.progress?.total || 1)) * 100)}%` }} /></div>
          <p className="text-xs text-[#5c6479]">Long clips are processed in overlapping chunks. A 10s clip usually takes about a minute.</p>
        </div>
      )}
      {st === "COMPLETED" && <Results session={session} onReset={reset} />}
      {st === "FAILED" && (
        <div className="py-8 space-y-3" data-testid="studio-failed">
          <p className="text-[#ff453a] text-sm">{session.error || "Matting failed."}</p>
          <button className="btn-ghost px-4 py-2 text-xs" onClick={reset}>Try another clip</button>
        </div>
      )}
    </div>
  );
}

export { SyncedTrio };
